//////////////////////////////////////////////////
/// AREA: ソースインポート
//////////////////////////////////////////////////

// 独自モジュール
import { FadeInEffect } from '../common/animation/FadeController';
import { SlideController } from '../common/animation/SlideController';
import { ClickEventHandler } from '../common/dom/_ClickEvent';
import { ClassListController } from '../common/dom/_ClassProcessor';
import { StyleController } from '../common/dom/_CssProperty';
import { GraphicsLazyController } from '../common/media/_Graphics';

// サイト機能
import { mountModalWindow } from '../connectors/modal-window';
import { CollapsePanel } from '../features/collapse';
import { TabSwitcher } from '../features/tab-switch';

//////////////////////////////////////////////////
/// AREA: グローバル変数
//////////////////////////////////////////////////

const gSiteBody: HTMLElement = document.body;
const gLazyTargets = [
  {
    selector: '.lazy_bg_image',
    options: {}
  },
  {
    selector: '.lazy_iframe',
    options: {
      rootMargin: '480px',
    }
  }
];

const gLazyController: GraphicsLazyController = new GraphicsLazyController(gLazyTargets);

let gScrollFlag: boolean = false;
let gResizeFlag: boolean = false;

//////////////////////////////////////////////////
/// AREA: トップページ
//////////////////////////////////////////////////

/// Part Name: ヒーロービュー
const frontHeroView = {
  INIT_OPACITY: 1,
  MIN_SCALE: 1,
  MAX_SCALE: 1.24,
  MAX_POSITION: 960,
  INTERVAL_OPACITY: 800,
  INTERVAL_SCALE: 2400,
  // mVisual: document.getElementById('primaryHeroView')?.querySelector('.visual')?.querySelector('.image') as HTMLElement | null,
  mVisual: document.getElementById('primaryHeroView')?.querySelector('.visual')?.querySelector('.bg_image') as HTMLElement | null,
  mWindowHeight: window.innerHeight,
  StyleController: new StyleController(),

  scrollAnimation(): void
  {
    const elem_style: CSSStyleDeclaration | undefined = this.mVisual?.style;

    this.applyFadeOut(elem_style);
    // mVisualがbackground-image: background, img要素: object を指定
    // this.applyPosition(elem_style, 'background');
    // スクロール時大きく: increase, 小さく: decrease
    this.applyScale(elem_style, 'decrease');
  },

  applyFadeOut(style: CSSStyleDeclaration | undefined): void
  {
    const value: number = this.INIT_OPACITY - window.scrollY / this.INTERVAL_OPACITY;

    this.StyleController.clampValue(style, 'opacity', `${value}`, 0, this.INIT_OPACITY);
  },

  applyPosition(style: CSSStyleDeclaration | undefined, property: string = 'object'): void
  {
    const inset_posY: number = window.scrollY;
    const elem_posY: number = - window.scrollY / 2;

    this.StyleController.clampValue(style, 'inset-block-start', `${inset_posY}px`, 0, this.MAX_POSITION,);

    if (property === 'object')
    {
      const prop_pos: string = 'object-position';
      this.StyleController.clampValue(style, prop_pos, `bottom ${elem_posY}px`, - this.MAX_POSITION / 2, 0);
    }
    else if (property === 'background')
    {
      const prop_pos: string = 'background-position-y';
      this.StyleController.clampValue(style, prop_pos, `${elem_posY}px`, - this.MAX_POSITION / 2, 0,);
    }
  },

  applyScale(style: CSSStyleDeclaration | undefined, direction: 'increase' | 'decrease'): void
  {
    const value: number =
      direction === 'increase'
        ? this.MIN_SCALE + window.scrollY / this.INTERVAL_SCALE
        : this.MAX_SCALE - window.scrollY / this.INTERVAL_SCALE;

    this.StyleController.clampValue(style, 'scale', `${value}`, this.MIN_SCALE, this.MAX_SCALE);
  },
};

//////////////////////////////////////////////////
/// AREA: Webサイト共通
//////////////////////////////////////////////////

/// Part Name: スクロールエフェクト
const gScrollFadeIn = {
  fromLeft: {
    FadeInEffect: new FadeInEffect(gSiteBody.getElementsByClassName('from_left')),
    onAnimation(): void
    {
      // IntersectionObserver を使ってフェードインを登録
      this.FadeInEffect.observe('fadeInFromLeft');
    }
  },
  fromBottom: {
    FadeInEffect: new FadeInEffect(gSiteBody.getElementsByClassName('from_bottom')),
    onAnimation(): void
    {
      this.FadeInEffect.observe('fadeInFromBottom');
    }
  },
  slideDoor: {
    FadeInEffect: new FadeInEffect(gSiteBody.getElementsByClassName('slide_door')),
    onAnimation(): void
    {
      this.FadeInEffect.observe('fadeInSlideDoor');
    }
  }
};

/// Part Name: グローバルナビ
const gHeaderNav = {
  ClickEventHandler: new ClickEventHandler(),
  mIcon: document.getElementById('gHeaderNavIcon'),
  mNav: document.getElementById('gHeaderNav'),
  mNavItems: null as HTMLCollectionOf<Element> | null,
  mToggleClassName: 'is_active',

  main(): void
  {
    if (this.clickToToggleDisplay)
    {
      this.clickToToggleDisplay();
    }
    if (this.clickToCloseDisplay)
    {
      this.clickToCloseDisplay();
    }
  },
  clickToToggleDisplay(): void
  {
    if (this.mIcon && this.mNav)
    {
      this.ClickEventHandler.mTrigger = this.mIcon;
      this.ClickEventHandler.mTarget = this.mNav;
      this.ClickEventHandler.toggleClassOnTarget(this.mToggleClassName);
    }
  },

  clickToCloseDisplay(): void
  {
    if (this.mNav)
    {
      this.mNavItems = this.mNav.getElementsByClassName('item');
      for (const item of this.mNavItems)
      {
        item.addEventListener('click', () =>
        {
          if (this.mIcon?.classList.contains(this.mToggleClassName) && this.mNav?.classList.contains(this.mToggleClassName))
          {
            this.mIcon.classList.remove(this.mToggleClassName);
            this.mNav.classList.remove(this.mToggleClassName);
          }
        });
      }
    }
  }
};

/// Part Name: サイドナビ
const gSideNav = {
  BREAK_POINT: 1201,
  mNav: document.getElementById('gSideNav'),

  scrollFixed(): void
  {
    if (this.mNav && window.innerWidth >= this.BREAK_POINT)
    {
      const nav_rect: DOMRect = this.mNav.getBoundingClientRect();
      const nav_height: number = nav_rect.height;
      const move_posY: string = 'calc(50% - ' + nav_height / 2 + 'px)';
      const nav_top: number = window.scrollY + nav_rect.top;

      if (window.scrollY > nav_top / 2)
      {
        this.mNav.style.position = 'fixed';
        this.mNav.style.insetBlockEnd = move_posY;
      }
      else
      {
        this.mNav.style.position = 'absolute';
        this.mNav.style.removeProperty('inset-block-end');
      }
    }
  }
};

/// Part Name: バックスクロールボタン
const gScrollTopBtn = {
  ClassListController: new ClassListController(),
  ClickEventHandler: new ClickEventHandler(),
  mButton: document.getElementById('gScrollTopBtn'),
  isDisplayed: false,
  onAnimation(target_height = 200): void
  {
    this.ClassListController.mTarget = this.mButton;
    const should_display: boolean = window.scrollY > target_height;

    if (should_display !== this.isDisplayed)
    {
      this.isDisplayed = should_display;
      if (should_display)
      {
        if (this.mButton) this.mButton.style.visibility = 'visible';
        this.ClassListController.switchClass('fadeOutDisp', 'fadeInDisp');
      }
      else
      {
        this.ClassListController.switchClass('fadeInDisp', 'fadeOutDisp');
      }
    }
  },

  toPageTop(): void
  {
    this.ClickEventHandler.mTrigger = this.mButton;
    this.ClickEventHandler.scrollToPageTop();
  }
};

/// Parts Name: 関連サービスボタン
const shop_button = document.querySelectorAll('.g-btn-related');

for (const button of shop_button)
{
  // button.addEventListener('click', () =>
  // {
  //   window.open(button.dataset.url, '_blank');
  // });
  if (button instanceof HTMLElement)
  {
    let scroll_timer: number | null = null;

    window.addEventListener('scroll', () =>
    {
      button.classList.add('is_hidden');

      if (scroll_timer !== null)
      {
        window.clearTimeout(scroll_timer);
      }

      scroll_timer = window.setTimeout(() =>
      {
        button.classList.remove('is_hidden');
      }, 400);
    });
  }
}

/// Part Name: コピーライト
const gCopyright = {
  SINCE_YEAR: 2025,
  mDispYear: document.getElementById('dispYear'),
  iDate: new Date(),

  getNowYear(): number
  {
    const now_year: number = this.iDate.getFullYear();
    return now_year;
  },

  dispYear(): void
  {
    if (this.mDispYear)
    {
      if (this.SINCE_YEAR === this.getNowYear())
      {
        this.mDispYear.innerHTML += this.SINCE_YEAR;
      }
      else
      {
        this.mDispYear.innerHTML += `${this.SINCE_YEAR} - ${this.getNowYear()}`;
      }
    }
  }
};


//////////////////////////////////////////////////
/// AREA: debug
//////////////////////////////////////////////////

class SlideDisplay
{
  public readonly mSlides: HTMLElement | null;
  public readonly mSlideItems: HTMLCollectionOf<HTMLElement>;

  private mCurrentSlide: number = 0;
  // setInterval()が返す値の型は、ブラウザとNode.js環境では異なる
  // （ブラウザではnumber、Node.jsではNodeJS.Timeout）
  private mIntervalId: number | NodeJS.Timeout | null;
  private readonly mChangeverTime: number;

  constructor(target_slides_id: HTMLElement | null, changeover_time: number = 5000)
  {
    // インスタンスが生成された時にスライド要素を初期化
    this.mSlides = target_slides_id;
    this.mSlideItems = this.mSlides?.getElementsByClassName('slide') as HTMLCollectionOf<HTMLElement>;
    // 最初のスライドのインディックスは0
    this.mCurrentSlide = 0;
    // setIntervalのIDを保持する変数。初期値はnull
    this.mIntervalId = null;
    this.mChangeverTime = changeover_time;
  }

  /**
   * // Method: スライドを初期化
   */
  private resetSlides(): void
  {
    if (this.mSlideItems)
    {
      // 全てのスライドから'is_active'クラスを削除し、z-indexを1に設定
      for (const slide of this.mSlideItems)
      {
        slide.classList.remove('is_active');
        slide.style.zIndex = '1';
      }
    }
  }

  /**
   * // Method: 指定されたインデックスのスライドを表示
   *
   * @param index スライドのインデックス
   */
  private showSlide(index: number = this.mCurrentSlide): void
  {
    // スライドを初期化
    this.resetSlides();

    if (this.mSlideItems)
    {
      // 指定されたインディックスのスライドを表示
      const slide: HTMLElement = this.mSlideItems[index];
      slide.classList.add('is_active');  // 'is_active' クラスを追加
      slide.style.zIndex = '2';          // z-indexを2に設定
    }
  }

  /**
   * // Method: 次のスライドを表示
   */
  private nextSlide(): void
  {
    if (this.mSlideItems)
    {
      // 現在のスライドインディックスを更新し、スライドショーを表示
      this.mCurrentSlide = (this.mCurrentSlide + 1) % this.mSlideItems.length;
    }
    this.showSlide(this.mCurrentSlide);
  }

  /**
   * // Method: 自動スライドショーを開始
   */
  public startAutoSlide(): void
  {
    this.showSlide(); // 初回表示
    // 指定した時間ごとに次のスライドに切り替える
    this.mIntervalId = setInterval(() => this.nextSlide(), this.mChangeverTime);
  }
}

const SlideShow = new SlideDisplay(document.getElementById('slideHeroView'));


const initializeModalWindows = (): void =>
{
  // const modal_windows: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>('[data-modal-window]');
  const modal_root: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>('[data-modal-root]');

  modal_root.forEach((modal_window: HTMLElement) =>
  {
    mountModalWindow(modal_window, {
      indicator: {
        enabled: true,
        item: true,
        group: false,
        create: true,
        placement: 'top-start',
      },
      choose: {
        mode: 'single-viewer',
      },
      // contentNavVisibility: {
      //   enabled: false,
      // },
      // uiMotion: {
      //   enabled: false,
      // },
      effect: {
        // window: {
        //   enabled: true,
        //   name: 'blur',
        //   fade: true,
        // },
        // content: {
        //   enabled: true,
        //   name: 'fade',
        // },
      },
    });
  });
};

document.addEventListener('DOMContentLoaded', () =>
{
  initializeModalWindows();
});

//////////////////////////////////////////////////
/// AREA: イベント、メソッドの実行
//////////////////////////////////////////////////

document.addEventListener('DOMContentLoaded', () =>
{
  gLazyController.loadGraphics();
  if (gHeaderNav.clickToToggleDisplay)
  {
    gHeaderNav.clickToToggleDisplay();
  }
  if (gHeaderNav.clickToCloseDisplay)
  {
    gHeaderNav.clickToCloseDisplay();
  }

  gSideNav.scrollFixed();
  gScrollTopBtn.toPageTop();
  gScrollTopBtn.onAnimation();
  gScrollFadeIn.fromLeft.onAnimation();
  gScrollFadeIn.fromBottom.onAnimation();
  gScrollFadeIn.slideDoor.onAnimation();
  gCopyright.dispYear();

  SlideShow.startAutoSlide();
});

window.addEventListener('scroll', () =>
{
  if (!gScrollFlag)
  {
    window.requestAnimationFrame(() =>
    {
      gScrollTopBtn.onAnimation();

      gScrollFlag = false;
    });
    gScrollFlag = true;
  }
});

window.addEventListener('resize', () =>
{
  if (!gResizeFlag)
  {
    window.requestAnimationFrame(() =>
    {
      gScrollTopBtn.onAnimation();

      gScrollFlag = false;
    });
    gResizeFlag = true;
  }
});

//////////////////////////////////////////////////
/// AREA: END
//////////////////////////////////////////////////
